import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ChevronDown, Pencil, Plus, Check, X, Tags } from 'lucide-react';

/**
 * MyBillBook-style category selector.
 * value: selected category name ('' = all). onChange(name).
 */
export default function CategoryMenu({ value = '', onChange }) {
  const { user } = useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editKey, setEditKey] = useState(null);
  const [editName, setEditName] = useState('');
  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER';

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get('/categories')).data.data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ['categories'] }); qc.invalidateQueries({ queryKey: ['garments'] }); };

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post('/categories', { name });
      toast.success(`Category "${name}" added`);
      setNewName(''); setAdding(false); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveRename = async (oldName) => {
    const name = editName.trim();
    if (!name) return;
    try {
      const r = await api.post('/categories/rename', { old_name: oldName, new_name: name });
      toast.success(`Renamed to "${name}"${r.data.data.garments_updated ? ` · ${r.data.data.garments_updated} items updated` : ''}`);
      if (value === oldName) onChange?.(name);
      setEditKey(null); setEditName(''); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="category-menu-trigger"
          className="flex h-11 min-w-[180px] items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 text-sm outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
          <span className="flex items-center gap-2 truncate">
            <Tags className="h-4 w-4 text-muted-foreground" />
            <span className={value ? 'font-medium' : 'text-muted-foreground'}>{value || 'Search Categories'}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2" data-testid="category-menu-content">
        <button data-testid="category-option-all" onClick={() => { onChange?.(''); setOpen(false); }}
          className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent ${!value ? 'font-semibold text-primary' : ''}`}>
          <span>All Categories</span>{!value && <Check className="h-4 w-4" />}
        </button>
        <div className="my-1 h-px bg-border" />
        <div className="max-h-64 overflow-y-auto pr-0.5">
          {cats.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">No categories yet.</p>}
          {cats.map((c) => (
            <div key={c.name} className="group flex items-center gap-1 rounded-lg px-1 hover:bg-accent">
              {editKey === c.name ? (
                <div className="flex flex-1 items-center gap-1 py-1">
                  <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(c.name); if (e.key === 'Escape') setEditKey(null); }}
                    className="h-8 rounded-md" data-testid={`category-edit-input-${c.name}`} />
                  <button onClick={() => saveRename(c.name)} data-testid={`category-edit-save-${c.name}`} className="flex h-8 w-8 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditKey(null)} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <button data-testid={`category-option-${c.name}`} onClick={() => { onChange?.(c.name); setOpen(false); }}
                    className={`flex flex-1 items-center justify-between rounded-md px-2 py-2 text-left text-sm ${value === c.name ? 'font-semibold text-primary' : ''}`}>
                    <span className="truncate">{c.name}</span>
                    <span className="ml-2 shrink-0 text-xs text-muted-foreground">({c.count})</span>
                  </button>
                  {canManage && (
                    <button data-testid={`category-edit-${c.name}`} onClick={() => { setEditKey(c.name); setEditName(c.name); }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <>
            <div className="my-1 h-px bg-border" />
            {adding ? (
              <div className="flex items-center gap-1 p-1">
                <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') setAdding(false); }}
                  placeholder="New category name" className="h-9 rounded-lg" data-testid="category-add-input" />
                <Button size="sm" className="h-9 rounded-lg" onClick={addCategory} data-testid="category-add-save">Add</Button>
              </div>
            ) : (
              <button data-testid="category-add-open" onClick={() => setAdding(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/50 py-2 text-sm font-medium text-primary hover:bg-primary/5">
                <Plus className="h-4 w-4" /> Add Category
              </button>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
